import { BrowserModule } from "@angular/platform-browser";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";

import { AppComponent } from "./components/app.component";
import { AppRoutingModule } from "./app-routing.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { LoginComponent } from "./components/login/login.component";
import { MaterialModule } from "./material.module";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NotFoundComponent } from "./components/not-found/not-found.component";

import {
  MsalModule,
  MsalRedirectComponent,
  MsalGuard,
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalBroadcastService,
} from "@azure/msal-angular";
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from "@azure/msal-browser";
import { EnrolledCoursesComponent } from "./components/enrolled-courses/enrolled-courses.component";
import { CoursesCatalogAdminModule } from "./cursos/coursesCatalogAdmin.module";
import { environment } from "./../environments/environment";
import { HomeComponent } from "./components/home/home.component";
import { MyQrsCoursesCatalogModule } from "./misQr/myQrsCoursesCatalog.module";
import { ConfirmationAlertDialogComponent } from "./components/confirmation-alert-dialog/confirmation-alert-dialog.component";
import { SharedModule } from "./shared/shared.module";
import { CalendarModule } from "./calendar/calendar.module";
import { AttendanceRecordModule } from "./attendanceRecord/attendanceRecord.module";
import { ReportsModule } from "./reports/reports.module";
import { AuthModule } from "./auth/auth.module";
import { TokenInterceptor } from "./auth/token.interceptor";
import { InvalidSessionInterceptor } from "./auth/sessionvalidator.interceptor";

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

const GRAPH_ENDPOINT = environment.Azure.GraphEndpoint;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.Azure.ActiveDirectory.ClientId,
      authority: environment.Azure.ActiveDirectory.Authority,
      redirectUri: environment.Azure.ActiveDirectory.RedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(GRAPH_ENDPOINT, ["user.read"]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ["user.read"],
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ConfirmationAlertDialogComponent,
    EnrolledCoursesComponent,
    HomeComponent,
    LoginComponent,
    NotFoundComponent,
  ],
  imports: [
    AppRoutingModule,
    AttendanceRecordModule,
    AuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CalendarModule,
    CoursesCatalogAdminModule,
    FontAwesomeModule,
    FormsModule,
    HttpClientModule,
    MaterialModule,
    MsalModule,
    MyQrsCoursesCatalogModule,
    NgbModule,
    ReactiveFormsModule,
    ReportsModule,
    SharedModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InvalidSessionInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
